// import { IPostTargetState, ISocialMediaConnection, PostTargetStatus } from '../../extViewmodels';

// Import base types from SDK
import type {
	IBulkOperationResult,
	IFileAttachment,
	IOperationResult,
	IOperationResultNoValue,
	IRawRichTextContentState,
	IBaseApiModel as SDKBaseApiModel,
	IBaseApiTypeModel as SDKBaseApiTypeModel,
} from '../../extViewmodels/sdk/models';

export type ViewTypes = 'SocialMediaPost';
export type ApiModelType = ViewTypes;

// Constrained version of IBaseApiTypeModel to preserve ViewTypes constraint
export interface IBaseApiTypeModel extends Omit<SDKBaseApiTypeModel, '_type'> {
	_type?: ApiModelType;
}

// Constrained version of IBaseApiModel to preserve ViewTypes constraint
export interface IBaseApiModel extends Omit<SDKBaseApiModel, '_type'>, IBaseApiTypeModel {
	// id and lastModifiedDate are already included from SDKBaseApiModel via Omit
}

// Re-export types from SDK for backward compatibility
export type {
	IBulkOperationResult,
	IFileAttachment,
	IOperationResult,
	IOperationResultNoValue,
	IRawRichTextContentState,
};

export const asApiError = (e: any) => {
	let error: IOperationResultNoValue = { success: false };
	if (e) {
		if (Object.prototype.hasOwnProperty.call(e, 'systemMessage')) {
			if (Object.prototype.hasOwnProperty.call(e, 'systemCode')) {
				error = e as IOperationResultNoValue;
				error.success = false;
			} else {
				error.systemMessage = e.systemMessage;
			}
		} else if (e instanceof Error) {
			error.systemMessage = e.message;
		} else if (typeof e === 'string') {
			error.systemMessage = e;
		} else if (Object.prototype.hasOwnProperty.call(e, 'toString') && typeof e.toString === 'function') {
			error.systemMessage = e.toString();
		} else {
			error.systemMessage = 'Unexpected error.';
		}

		if (!error.systemCode && (error.systemMessage || '').toLocaleLowerCase() === 'failed to fetch') {
			error.systemCode = -1;
			error.systemMessage = 'Error connecting to Levitate.';
		}
	} else {
		error.systemMessage = 'Unexpected error.';
	}

	return error;
};

export enum HTTPMethod {
	DELETE = 'DELETE',
	GET = 'GET',
	PATCH = 'PATCH',
	POST = 'POST',
	PUT = 'PUT',
}

export interface IApiClientConfig {
	accessToken?: string;
	baseUrl: string;
}

export class ApiClient {
	protected mConfig: IApiClientConfig;

	constructor(config: IApiClientConfig) {
		this.mConfig = config;
	}

	public get baseUrl() {
		return this.mConfig.baseUrl;
	}

	public get accessToken() {
		return this.mConfig.accessToken;
	}

	public get = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.GET));
	};

	public put = <T>(path: string, body?: T) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PUT, body));
	};

	public patch = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PATCH, body));
	};

	public delete = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.DELETE));
	};

	public post = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.POST, body));
	};

	protected buildFetchRequest = (path: string, method: HTTPMethod, obj: any = null) => {
		// Check for a special method called FILEPOST, which we will transform to POST but will not convert body to JSON
		const isFormDataBody = obj instanceof FormData;

		// Define headers
		const headers = new Headers();
		if (isFormDataBody) {
			// DO NOT INCLUDE A HEADER FOR FILE POSTS
		} else {
			headers.append('content-type', 'application/json; charset=utf-8');
		}
		headers.append('accept', 'application/json');
		headers.append('Pragma', 'no-cache');
		headers.append('Cache-Control', 'no-cache');
		if (this.mConfig.accessToken) {
			headers.append('Authorization', 'bearer ' + this.mConfig.accessToken);
		}
		const url = `${this.mConfig.baseUrl}/${path}`;
		const config: RequestInit = {
			headers,
			method,
			mode: 'cors',
			redirect: 'follow',
		};

		if (method.toLowerCase() !== 'get' && !!obj) {
			config.body = isFormDataBody ? obj : JSON.stringify(obj);
		}

		const request = new Request(url, config);
		return request;
	};

	protected executeRequest = async <T>(request: Request): Promise<[IOperationResult<T>, IOperationResultNoValue]> => {
		try {
			const response = await fetch(request);
			const apiResponse: IOperationResult<T> = await response.json();
			if (!apiResponse.success) {
				return [null, apiResponse];
			}

			return [apiResponse, null];
		} catch (error) {
			return [null, asApiError(error) as IOperationResultNoValue];
		}
	};
}

export interface IPublicNewsletterView {
	subject: string;
	htmlContent: string;
	/** ISO date when the campaign was sent, or null if it hasn't been sent yet (drafts/scheduled). */
	sentDate: string | null;
	fromName: string;
	accountName: string;
}

export interface ISocialMediaPost extends IBaseApiModel {
	anonymousAccessLink?: string;
	attachments?: IFileAttachment[];
	citation?: string;
	content: IRawRichTextContentState;
	designatedTargets?: IPostTarget[];
	dueDate?: string;
	name?: string;
	targets?: IPostTarget[];
	overlayLogo?: boolean;
	/** Empty unless the post is split; then each platform publishes its own entry and the base publishes nowhere. */
	platformCustomizations?: IPostPlatformCustomization[];
}

export interface IPostPlatformCustomization {
	platform: SocialMediaType;
	content: IRawRichTextContentState;
	attachments?: IFileAttachment[];
}

export interface IAnonymousSocialMediaPost extends ISocialMediaPost {
	logoUrl?: string;
	hideWhiteBackground?: boolean;
	resolvedSignature?: IRawRichTextContentState;
	resolvedDisclosure?: string;
}

export interface IPostTarget {
	provider: SocialMediaType;
	pageId?: string;
	userId?: string;
	state?: IPostTargetState;
}

export interface IPostTargetState {
	status: PostTargetStatus;
	lastAttempted?: string;
	lastConnectionState?: ISocialMediaConnection;
	attempts?: number;
	lastErrorMessage?: string;
}

export enum PostTargetStatus {
	Unknown = 'Unknown',
	Posted = 'Posted',
	Failed = 'Failed',
	Pending = 'Pending',
}

export enum SocialMediaType {
	Unknown = 'Unknown',
	Facebook = 'Facebook',
	Instagram = 'Instagram',
	LinkedIn = 'LinkedIn',
}

export interface ISocialMediaConnection {
	encryptedBlobId?: string;
	expiresAt?: string; // Date
	postTargetDisplayName?: string;
	postTargetId?: string;
	state?: SocialMediaConnectionState;
	type: SocialMediaType;
	userId?: string;
	userName?: string;
}

export const SupportedSocialMediaTypes = [
	SocialMediaType.Facebook,
	SocialMediaType.Instagram,
	SocialMediaType.LinkedIn,
];

export enum SocialMediaConnectionState {
	Connected = 'Connected',
	Disconnected = 'Disconnected',
	NoTarget = 'NoTarget',
	Unknown = 'Unknown',
}

// IFileAttachment and IRawRichTextContentState are now imported from SDK
